import classNames from 'classnames';
import React from 'react';

import {AnalyticsEvent, deprecatedTrackEvent} from 'analytics/Analytics';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import makeTableCSV from 'notebook/tables/export/makeTableCSV';
import {Cells} from 'notebook/tables/TableConstants';
import Tooltip, {TooltipProps} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {initiateTextDownload} from 'util/TextToBlobUtil';

// Give us anything you like; we'll pass these params to the onDownload handler
type OnDownloadCSVParams = unknown;

interface SimpleDownloadAsCSVButtonProps {
  buttonStyle?: ButtonStyle;
  className?: string;
  size?: ButtonSize;
  disabled?: boolean;
  onDownloadAsCSV: (params?: OnDownloadCSVParams) => void;
  tooltipProps?: Partial<TooltipProps>;
  // These params are returned to the caller in `onDownloadAsCSV`. This is used to help components
  // be more closure-free. I.e., without this trick, components need their own inline function
  // that will be recreated on each render, preventing shouldUpdate optimizations.
  onDownloadParams?: OnDownloadCSVParams;
}

/**
 * Presentational DownloadAsCSVButton component
 */
export const SimpleDownloadAsCSVButton: React.FunctionComponent<SimpleDownloadAsCSVButtonProps> = ({
  buttonStyle = ButtonStyle.TERTIARY_GRAY,
  className,
  size = ButtonSize.MINI,
  disabled = false,
  onDownloadAsCSV,
  tooltipProps,
  onDownloadParams,
}) => (
  <Tooltip title="Download as CSV" {...tooltipProps}>
    <span>
      <Button
        buttonStyle={buttonStyle}
        className={classNames('u-mini-spacing-left', className)}
        disabled={disabled}
        icon="download"
        onClick={() => {
          onDownloadAsCSV(onDownloadParams);
        }}
        size={size}
      />
    </span>
  </Tooltip>
);
SimpleDownloadAsCSVButton.displayName = 'SimpleDownloadAsCSVButton';

interface DownloadAsCSVButtonProps {
  tableLabel: string;
  disabled: boolean;
  analyticsEvent: AnalyticsEvent;
  table: Cells;
  columnLabels: Array<string>;
  computedTable: Array<Array<EvaluatedCell>> | null;
}

const DownloadAsCSVButton: React.FunctionComponent<DownloadAsCSVButtonProps> = (props) => {
  async function handleDownloadAsCSV(): Promise<void> {
    const {table, columnLabels} = props;
    initiateTextDownload(
      await makeTableCSV({table, computedTable: props.computedTable!, headerRow: columnLabels}),
      `${props.tableLabel}.csv`
    );
    deprecatedTrackEvent(props.analyticsEvent);
  }

  return (
    <SimpleDownloadAsCSVButton
      disabled={props.disabled || props.computedTable == null}
      onDownloadAsCSV={handleDownloadAsCSV}
    />
  );
};

export default DownloadAsCSVButton;
